import type {DispatchCenterOption, DispatchCenterType, UserCreateFormMetadata} from "../types";

function isDispatchCenterType(value: unknown): value is DispatchCenterType {
  return value === "internal" || value === "third_party";
}

export function normalizeDispatchCenterOption(raw: unknown): DispatchCenterOption | null {
  if (!raw || typeof raw !== "object") return null;

  const row = raw as Record<string, unknown>;
  const idRaw = row.id ?? row.dispatch_center_id ?? row.value ?? row.key;
  const id = Number(idRaw);
  if (!Number.isFinite(id)) return null;

  const name =
    typeof row.name === "string"
      ? row.name
      : typeof row.label === "string"
        ? row.label
        : `Center #${id}`;

  const statusRaw = row.status;
  const status = statusRaw === 0 || statusRaw === 1 ? statusRaw : undefined;
  const type = isDispatchCenterType(row.type) ? row.type : undefined;

  return {
    id,
    name,
    ...(type ? {type} : {}),
    ...(status !== undefined ? {status} : {}),
  };
}

export function normalizeDispatchCenterOptions(raw: unknown): DispatchCenterOption[] {
  if (Array.isArray(raw)) {
    return raw
      .map(normalizeDispatchCenterOption)
      .filter((option): option is DispatchCenterOption => option != null);
  }

  if (!raw || typeof raw !== "object") return [];

  const record = raw as Record<string, unknown>;

  if (Array.isArray(record.data)) {
    return normalizeDispatchCenterOptions(record.data);
  }

  return Object.entries(record)
    .map(([key, value]) => {
      if (typeof value === "string") {
        const id = Number(key);
        return Number.isFinite(id) ? {id, name: value} : null;
      }

      const nested =
        value && typeof value === "object" ? (value as Record<string, unknown>) : {};
      return normalizeDispatchCenterOption({...nested, id: nested.id ?? key});
    })
    .filter((option): option is DispatchCenterOption => option != null);
}

export function normalizeUserCreateFormMetadata(raw: unknown): UserCreateFormMetadata {
  const record = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};

  return {
    roles: (Array.isArray(record.roles) ? record.roles : []) as UserCreateFormMetadata["roles"],
    dispatch_centers: normalizeDispatchCenterOptions(record.dispatch_centers),
  };
}
